import { useCallback, useEffect, useRef, useState } from 'react';
import _debounce from 'lodash/debounce';
import { PageView, UserInteractionEvent } from '../../../models/analytics';
import {
  ProviderSortingStrategy,
  ProviderViewSetGroup,
} from '../../../utilities/provider-view-set';
import { useAnalytics } from '../../Analytics/AnalyticsProvider';
import { ProviderEntity } from '../../../models/provider';
import {
  InteractionElement,
  parentMessenger,
} from '../../Analytics/ParentApplicationEventMessenger';

export type ProviderViewSetArgs = {
  providers: ProviderEntity[];
  version: string;
  providerSortingStrategy?: ProviderSortingStrategy;
};

export type ProviderGroupFilter = (
  providerViewSetArgs: ProviderViewSetArgs,
  str: string
) => ProviderViewSetGroup[];

export type UseProviderSearchPayload = {
  pageView: PageView;
  providerViewSetArgs: ProviderViewSetArgs;
  filterFn: ProviderGroupFilter;
  delay?: number;
};

export const PROVIDER_SEARCH_DEFAULT_DELAY = 500;

export const useProviderSearch = ({
  pageView,
  providerViewSetArgs,
  filterFn,
  delay = PROVIDER_SEARCH_DEFAULT_DELAY,
}: UseProviderSearchPayload) => {
  const { tinklinkAnalytics } = useAnalytics();
  const [searchString, setSearchString] = useState('');
  const [searchHistory, setSearchHistory] = useState<Record<string, number>>();
  const [searchAnalyticSent, setSearchAnalyticSent] = useState(false);
  const [providerViewSetGroups, setProviderViewSetGroups] = useState<ProviderViewSetGroup[]>(() =>
    filterFn(providerViewSetArgs, '')
  );

  const sendInputChangeAnalytic = useCallback(() => {
    tinklinkAnalytics.sendInteractionEvent({
      action: `${pageView}/${UserInteractionEvent.INPUT_CHANGE}`,
      view: pageView,
    });

    parentMessenger.fireInteractionEvent({
      pageView,
      element: InteractionElement.INPUT_FIELD,
    });
    setSearchAnalyticSent(true);
  }, [pageView, tinklinkAnalytics]);

  const onSearchChange = (newSearchString: string) => {
    const newProviderViewSetGroups = filterFn(providerViewSetArgs, newSearchString);
    setProviderViewSetGroups(newProviderViewSetGroups);
    setSearchString(newSearchString);

    setSearchHistory((prevSearchHistory) => ({
      ...prevSearchHistory,
      [newSearchString]: newProviderViewSetGroups.length,
    }));

    if (!searchAnalyticSent) sendInputChangeAnalytic();
  };

  const onSearchChangeDebounced = useRef(_debounce(onSearchChange, delay));

  const sendSearchHistoryAnalytic = useCallback(() => {
    if (!searchHistory) return;

    tinklinkAnalytics.sendInteractionEvent({
      action: `${pageView}/${UserInteractionEvent.INPUT_CHANGE}`,
      view: pageView,
      label: JSON.stringify(searchHistory),
    });

    parentMessenger.fireInteractionEvent({
      pageView,
      element: InteractionElement.INPUT_FIELD,
    });
  }, [searchHistory, tinklinkAnalytics, pageView]);

  useEffect(() => {
    window.addEventListener('beforeunload', sendSearchHistoryAnalytic);

    return () => window.removeEventListener('beforeunload', sendSearchHistoryAnalytic);
  }, [sendSearchHistoryAnalytic]);

  return {
    providerViewSetGroups,
    onSearchChange: onSearchChangeDebounced.current,
    searchString,
    searchHistory,
    sendSearchHistoryAnalytic,
  };
};
